import { nflTeams, collegeTeams, nbaTeams, mlbTeams, nhlTeams, cbbTeams } from '../data/teams';
import {
  getFootballSeasonYear,
  getBasketballHockeySeasonYear,
  getBaseballSeasonYear,
} from './season';

const CACHE_TTL_MS = 30 * 60 * 1000;
const cache = new Map();

const findType = (teamName) => {
  if (nflTeams.some(team => team.name === teamName)) return 'nfl';
  if (collegeTeams.some(team => team.name === teamName)) return 'college';
  if (nbaTeams.some(team => team.name === teamName)) return 'nba';
  if (mlbTeams.some(team => team.name === teamName)) return 'mlb';
  if (nhlTeams.some(team => team.name === teamName)) return 'nhl';
  if (cbbTeams?.some?.(team => team.name === teamName)) return 'cbb';
  return null;
};

const getSeason = (type) => {
  if (type === 'nfl' || type === 'college') return getFootballSeasonYear();
  if (type === 'nba' || type === 'nhl' || type === 'cbb') return getBasketballHockeySeasonYear();
  if (type === 'mlb') return getBaseballSeasonYear();
  return new Date().getFullYear();
};

const getSportLabel = (type) => ({
  nfl: 'NFL',
  college: 'College Football',
  nba: 'NBA',
  mlb: 'MLB',
  nhl: 'NHL',
  cbb: 'College Basketball',
}[type] || 'Sports');

const getCached = (key) => {
  const value = cache.get(key);
  if (!value) return null;
  if (Date.now() - value.savedAt > CACHE_TTL_MS) {
    cache.delete(key);
    return null;
  }
  return value.data;
};

const putCached = (key, data) => {
  cache.set(key, { savedAt: Date.now(), data });
};

const todayEastern = () => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const year = parts.find(part => part.type === 'year')?.value;
  const month = parts.find(part => part.type === 'month')?.value;
  const day = parts.find(part => part.type === 'day')?.value;
  return `${year}-${month}-${day}`;
};

class StatsHawkScheduleAPI {
  static supportsTeam(teamName) {
    return Boolean(findType(teamName));
  }

  static async getTeamSchedule(teamName) {
    const type = findType(teamName);
    if (!type) return null;

    const season = getSeason(type);
    const key = `${type}:${season}:${teamName}`;
    const cached = getCached(key);
    if (cached) return cached;

    const params = new URLSearchParams({
      team: teamName,
      type,
      season: String(season),
    });

    const response = await fetch(`/api/statshawk-schedule?${params.toString()}`);
    let data = {};
    try {
      data = await response.json();
    } catch {
      // Keep HTTP status as the useful failure signal below.
    }

    if (!response.ok) {
      const message = data?.message || data?.error || `HTTP ${response.status}`;
      throw new Error(`StatsHawk schedule failed: ${message}`);
    }

    const schedule = Array.isArray(data?.schedule) ? data.schedule : [];
    if (schedule.length === 0) {
      throw new Error(`StatsHawk returned no ${season} games for ${teamName}`);
    }

    const result = {
      schedule,
      sport: getSportLabel(type),
      provider: 'StatsHawk',
      competition: data?.competition || null,
      season: data?.season || season,
    };

    putCached(key, result);
    return result;
  }

  static async getUpcomingGames(teamName, limit = 6) {
    const full = await this.getTeamSchedule(teamName);
    if (!full) return null;

    const today = todayEastern();
    const upcoming = full.schedule.filter(game => game.date >= today && !game.isCompleted);

    return {
      ...full,
      schedule: upcoming.slice(0, limit),
      fullSchedule: full.schedule,
    };
  }

  static clearCache() {
    cache.clear();
  }

  static getCacheSize() {
    return cache.size;
  }
}

export default StatsHawkScheduleAPI;
